import { GoogleGenAI } from "@google/genai";
import { GEMINI_API_KEY, GEMINI_MODEL, GEMINI_CONFIG, GEMINI_SAFETY_SETTINGS } from "../constants/AskAIConstants";
import { LogFirebase } from "../services/firestore";
import { extractBase64Data } from "../utils/validateImage";

class AskAIAPI {
    constructor() {
        // Initialize Google Generative AI client (matching AI Studio structure)
        this.ai = new GoogleGenAI({
            apiKey: GEMINI_API_KEY,
        });
    }

    /**
     * Convert messages array to Gemini conversation history format
     * @param {Array} messages - Array of messages from Redux state
     * @returns {Array} - Formatted contents array for Gemini API
     */
    formatConversationHistory(messages) {
        const contents = [];

        messages.forEach((message) => {
            const parts = [];

            // Add image if present (with validation)
            if (message.image) {
                const extraction = extractBase64Data(message.image);
                if (extraction.success) {
                    parts.push({
                        inlineData: {
                            mimeType: extraction.mimeType || "image/jpeg",
                            data: extraction.data,
                        },
                    });
                } else {
                    console.error("Invalid image in conversation history:", extraction.error);
                }
            }

            // Add text if present
            if (message.text) {
                parts.push({
                    text: message.text,
                });
            }

            // Add to contents with appropriate role
            if (parts.length > 0) {
                contents.push({
                    role: message.type === "USER" ? "user" : "model",
                    parts: parts,
                });
            }
        });

        return contents;
    }

    /**
     * Build the complete config object matching AI Studio structure
     * Uses centralized safety settings from AskAIConstants
     * Issue #8: Fixed to use consistent safety filter thresholds
     * @returns {Object} - Complete configuration
     */
    buildConfig() {
        return {
            thinkingConfig: GEMINI_CONFIG.thinkingConfig,
            mediaResolution: GEMINI_CONFIG.mediaResolution,
            safetySettings: GEMINI_SAFETY_SETTINGS, // Use centralized safety settings
            tools: GEMINI_CONFIG.tools,
            systemInstruction: GEMINI_CONFIG.systemInstruction,
            generationConfig: GEMINI_CONFIG.generationConfig,
        };
    }

    /**
     * Send request to Gemini API with full conversation context using @google/genai
     * Structure matches AI Studio code exactly
     * @param {Array} conversationHistory - Full message history from Redux
     * @param {Object} newMessage - New message to send (contains image and/or text)
     * @returns {Promise} - API response
     */
    async sendWithContext(conversationHistory, newMessage) {
        try {
            LogFirebase("AskAI", "sendWithContext_started", "");

            // Convert conversation history to Gemini format
            const historyContents = this.formatConversationHistory(conversationHistory);

            // Build the new user message parts
            const newMessageParts = [];

            // Add image if present (with validation)
            if (newMessage.image) {
                const extraction = extractBase64Data(newMessage.image);
                if (!extraction.success) {
                    LogFirebase("AskAI", "sendWithContext_invalid_image", extraction.error);
                    return { success: false, error: extraction.error };
                }
                newMessageParts.push({
                    inlineData: {
                        mimeType: extraction.mimeType || "image/jpeg",
                        data: extraction.data,
                    },
                });
            }

            // Add text - use provided text or default for Quick Assist
            if (newMessage.text && newMessage.text.trim()) {
                newMessageParts.push({
                    text: newMessage.text,
                });
            }

            // Build complete contents array (history + new message)
            const contents = [
                ...historyContents,
                {
                    role: "user",
                    parts: newMessageParts,
                },
            ];

            // Get the complete config (matching AI Studio structure)
            const config = this.buildConfig();

            // Call the API (matching AI Studio structure)
            const response = await this.ai.models.generateContent({
                model: GEMINI_MODEL,
                config: config,
                contents: contents,
            });

            LogFirebase("AskAI", "sendWithContext_success", "");

            // Extract the text response
            const responseText = response?.text || "I couldn't generate a response.";

            return {
                success: true,
                data: responseText,
            };
        } catch (error) {
            console.error("Error calling Gemini API:", error);
            LogFirebase("AskAI", "sendWithContext_error", error?.message || "Unknown error");

            // Enhanced error handling
            let errorMessage = "Failed to get AI response";

            if (error?.message) {
                // Check for specific error types from @google/genai
                if (error.message.includes("RATE_LIMIT_EXCEEDED") || error.message.includes("429")) {
                    errorMessage = "Rate limit exceeded. Please try again in a moment.";
                } else if (error.message.includes("INVALID_ARGUMENT") || error.message.includes("400")) {
                    errorMessage = "Invalid request. Please check the image and try again.";
                } else if (error.message.includes("API_KEY") || error.message.includes("API key")) {
                    errorMessage = "API key error. Please check configuration.";
                } else if (error.message.includes("SAFETY") || error.message.includes("blocked")) {
                    errorMessage = "Content was blocked by safety filters.";
                } else if (error.message.includes("RECITATION")) {
                    errorMessage = "Response blocked due to citation policy.";
                } else {
                    errorMessage = error.message;
                }
            }

            return {
                success: false,
                error: errorMessage,
            };
        }
    }
}

export default new AskAIAPI();
